/**
 * Integrytas Group — PFW Third-Party Portal: two-phase login.
 *
 * Public, session-less screen (mounted at /portal). Phase 1 identifies the third
 * party (company number or name); phase 2 takes the user's credential within that
 * company. Entirely separate from the app's GoTrue login — a portal user is a
 * pfw_portal_user, not an app user.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { portalLogin, loadPortalSession, clearPortalSession, type PortalSession } from '../../lib/portal_auth';
import { PortalHomeShell } from './PortalHomeShell';

type Phase = 'company' | 'credentials';

export function PortalLoginPage() {
  const { t } = useTranslation();
  const [session, setSession] = useState<PortalSession | null>(() => loadPortalSession());
  const [phase, setPhase]     = useState<Phase>('company');
  const [company, setCompany] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const errorText = (code: string): string => {
    switch (code) {
      case 'PFW.PORTAL_AUTH_FAILED': return t('portal.err.auth_failed', 'Incorrect company, username or password.');
      case 'PFW.PORTAL_LOCKED':      return t('portal.err.locked', 'Account temporarily locked after too many attempts. Try again later.');
      case 'PFW.PORTAL_NETWORK_ERROR': return t('portal.err.network', 'Could not reach the portal. Check your connection and try again.');
      default: return t('portal.err.generic', 'Sign-in failed. Please try again.');
    }
  };

  const goToCredentials = () => {
    setError(null);
    if (!company.trim()) { setError(t('portal.err.company_required', 'Enter your company number or name.')); return; }
    setPhase('credentials');
  };

  const signIn = async () => {
    setError(null);
    if (!username.trim() || !password) { setError(t('portal.err.creds_required', 'Enter your username and password.')); return; }
    setBusy(true);
    const { session: s, error: e } = await portalLogin(company.trim(), username.trim(), password);
    setBusy(false);
    if (e || !s) { setError(errorText(e ?? 'PFW.PORTAL_AUTH_FAILED')); return; }
    setSession(s);
  };

  const signOut = () => {
    clearPortalSession();
    setSession(null);
    setPhase('company');
    setCompany(''); setUsername(''); setPassword('');
  };

  // ── Signed-in shell ────────────────────────────────────────────────────────
  if (session) {
    return <PortalHomeShell session={session} onSignOut={signOut} />;
  }

  // ── Two-phase login ────────────────────────────────────────────────────────
  return (
    <PortalShell>
      {phase === 'company' ? (
        <form onSubmit={e => { e.preventDefault(); goToCredentials(); }} className="space-y-3">
          <div className="mb-1 text-csm-lg font-semibold text-csm-ink">{t('portal.title', 'Partner Portal')}</div>
          <div className="mb-3 text-csm-sm text-csm-ink-soft">{t('portal.step1', 'Step 1 of 2 — identify your organisation')}</div>
          <label className="block text-csm-sm">
            <span className="mb-1 block text-csm-ink-soft">{t('portal.company', 'Company number or name')}</span>
            <input autoFocus value={company} onChange={e => setCompany(e.target.value)}
                   className="h-9 w-full rounded border border-csm-border px-3 text-csm-md focus:border-primary focus:outline-none" />
          </label>
          {error && <div className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-xs text-csm-danger">{error}</div>}
          <button type="submit" className="h-9 w-full rounded border border-primary bg-primary text-csm-sm font-medium text-white hover:bg-primary-hover">{t('portal.continue', 'Continue')}</button>
        </form>
      ) : (
        <form onSubmit={e => { e.preventDefault(); void signIn(); }} className="space-y-3">
          <div className="mb-1 text-csm-lg font-semibold text-csm-ink">{t('portal.title', 'Partner Portal')}</div>
          <div className="mb-3 flex items-center justify-between text-csm-sm text-csm-ink-soft">
            <span>{t('portal.step2', 'Step 2 of 2 — sign in')}</span>
            <button type="button" onClick={() => { setPhase('company'); setError(null); }} className="text-csm-xs text-primary hover:underline">{t('portal.change_company', 'Change')} ({company})</button>
          </div>
          <label className="block text-csm-sm">
            <span className="mb-1 block text-csm-ink-soft">{t('portal.username', 'Username')}</span>
            <input autoFocus value={username} onChange={e => setUsername(e.target.value)} autoComplete="username"
                   className="h-9 w-full rounded border border-csm-border px-3 text-csm-md focus:border-primary focus:outline-none" />
          </label>
          <label className="block text-csm-sm">
            <span className="mb-1 block text-csm-ink-soft">{t('portal.password', 'Password')}</span>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password"
                   className="h-9 w-full rounded border border-csm-border px-3 text-csm-md focus:border-primary focus:outline-none" />
          </label>
          {error && <div className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-xs text-csm-danger">{error}</div>}
          <button type="submit" disabled={busy} className="h-9 w-full rounded border border-primary bg-primary text-csm-sm font-medium text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('portal.signing_in', 'Signing in…') : t('portal.sign_in', 'Sign in')}</button>
        </form>
      )}
    </PortalShell>
  );
}

function PortalShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-csm-surface-alt px-4">
      <div className="w-full max-w-sm rounded-md border border-csm-border bg-white p-6 shadow-sm">
        {children}
      </div>
    </div>
  );
}
